import React, { useState, useEffect } from 'react';
import { studentApi } from '../../services/api';
import { Student } from '../../types';
import { handleApiError, handleSuccess, validateRequired, validateForm } from '../../utils/errorHandler';
import { formatDateForInput } from '../../utils/dateUtils';
import './StudentForm.css';

interface StudentFormProps {
  student?: Student | null;
  onSubmit: () => void;
  onCancel: () => void;
}

const StudentForm: React.FC<StudentFormProps> = ({ student, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    fathers_name: '',
    mothers_name: '',
    date_of_birth: '',
    nationality: '',
    mother_tongue: '',
    emirate: '',
    emirates_id: '',
    primary_contact: '',
    secondary_contact: '',
    email: '',
    joining_date: new Date().toISOString().split('T')[0], // Today's date as default
    therapy_needs: '',
    special_requirements: '',
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const emirateOptions = [
    'Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman', 'Umm Al Quwain', 'Ras Al Khaimah', 'Fujairah'
  ];

  useEffect(() => {
    if (student) {
      setFormData({
        first_name: student.first_name,
        last_name: student.last_name,
        fathers_name: student.fathers_name,
        mothers_name: student.mothers_name,
        date_of_birth: formatDateForInput(student.date_of_birth),
        nationality: student.nationality,
        mother_tongue: student.mother_tongue,
        emirate: student.emirate || '',
        emirates_id: student.emirates_id,
        primary_contact: student.primary_contact,
        secondary_contact: student.secondary_contact|| '',
        email: student.email,
        joining_date: formatDateForInput(student.joining_date),
        therapy_needs: student.therapy_needs || '',
        special_requirements: student.special_requirements || '',
        is_active: student.is_active
      });
    }
  }, [student]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const validateFormData = () => {
    const validationRules = {
      first_name: (value: string) => validateRequired(value, 'First Name'),
      last_name: (value: string) => validateRequired(value, 'Last Name'),
      fathers_name: (value: string) => validateRequired(value, "Father's Name"),
      mothers_name: (value: string) => validateRequired(value, "Mother's Name"),
      date_of_birth: (value: string) => validateRequired(value, 'Date of Birth'),
      nationality: (value: string) => validateRequired(value, 'Nationality'),
      mother_tongue: (value: string) => validateRequired(value, 'Mother Tongue'),
      emirates_id: (value: string) => validateRequired(value, 'Emirates ID'),
      primary_contact: (value: string) => validateRequired(value, 'Primary Contact'),
      email: (value: string) => {
        if (!value) return 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Please enter a valid email address';
        return '';
      },
      joining_date: (value: string) => validateRequired(value, 'Joining Date'),
    };

    return validateForm(formData, validationRules);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const validationErrors = validateFormData();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      if (student) {
        await studentApi.update(student.id, formData);
        handleSuccess('Student updated successfully');
      } else {
        await studentApi.create(formData);
        handleSuccess('Student created successfully');
      }
      onSubmit();
    } catch (err: any) {
      handleApiError(err, student ? 'Failed to update student' : 'Failed to create student');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>{student ? 'Edit Student' : 'Add New Student'}</h3>
          <button className="close-button" onClick={onCancel}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="student-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="first_name">First Name *</label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                className={`form-input ${errors.first_name ? 'error' : ''}`}
              />
              {errors.first_name && <span className="field-error">{errors.first_name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="last_name">Last Name *</label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                className={`form-input ${errors.last_name ? 'error' : ''}`}
              />
              {errors.last_name && <span className="field-error">{errors.last_name}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="fathers_name">Father's Name *</label>
              <input
                type="text"
                id="fathers_name"
                name="fathers_name"
                value={formData.fathers_name}
                onChange={handleChange}
                required
                className={`form-input ${errors.fathers_name ? 'error' : ''}`}
              />
              {errors.fathers_name && <span className="field-error">{errors.fathers_name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="mothers_name">Mother's Name *</label>
              <input
                type="text"
                id="mothers_name"
                name="mothers_name"
                value={formData.mothers_name}
                onChange={handleChange}
                required
                className={`form-input ${errors.mothers_name ? 'error' : ''}`}
              />
              {errors.mothers_name && <span className="field-error">{errors.mothers_name}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="date_of_birth">Date of Birth *</label>
              <input
                type="date"
                id="date_of_birth"
                name="date_of_birth"
                value={formData.date_of_birth}
                onChange={handleChange}
                required
                className={`form-input ${errors.date_of_birth ? 'error' : ''}`}
              />
              {errors.date_of_birth && <span className="field-error">{errors.date_of_birth}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="nationality">Nationality *</label>
              <input
                type="text"
                id="nationality"
                name="nationality"
                value={formData.nationality}
                onChange={handleChange}
                required
                className={`form-input ${errors.nationality ? 'error' : ''}`}
              />
              {errors.nationality && <span className="field-error">{errors.nationality}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="mother_tongue">Mother Tongue *</label>
              <input
                type="text"
                id="mother_tongue"
                name="mother_tongue"
                value={formData.mother_tongue}
                onChange={handleChange}
                required
                className={`form-input ${errors.mother_tongue ? 'error' : ''}`}
              />
              {errors.mother_tongue && <span className="field-error">{errors.mother_tongue}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="emirate">Emirate</label>
              <select
                id="emirate"
                name="emirate"
                value={formData.emirate}
                onChange={handleChange}
                className="form-input"
              >
                <option value="">Select Emirate</option>
                {emirateOptions.map(emirate => (
                  <option key={emirate} value={emirate}>{emirate}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="emirates_id">Emirates ID *</label>
              <input
                type="text"
                id="emirates_id"
                name="emirates_id"
                value={formData.emirates_id}
                onChange={handleChange}
                required
                placeholder="784-XXXX-XXXXXXX-X"
                className={`form-input ${errors.emirates_id ? 'error' : ''}`}
              />
              {errors.emirates_id && <span className="field-error">{errors.emirates_id}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className={`form-input ${errors.email ? 'error' : ''}`}
              />
              {errors.email && <span className="field-error">{errors.email}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="primary_contact">Primary Contact *</label>
              <input
                type="tel"
                id="primary_contact"
                name="primary_contact"
                value={formData.primary_contact}
                onChange={handleChange}
                required
                placeholder="+971-XX-XXX-XXXX"
                className={`form-input ${errors.primary_contact ? 'error' : ''}`}
              />
              {errors.primary_contact && <span className="field-error">{errors.primary_contact}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="secondary_contact">Secondary Contact</label>
              <input
                type="tel"
                id="secondary_contact"
                name="secondary_contact"
                value={formData.secondary_contact}
                onChange={handleChange}
                placeholder="+971-XX-XXX-XXXX"
                className="form-input"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="joining_date">Joining Date *</label>
              <input
                type="date"
                id="joining_date"
                name="joining_date"
                value={formData.joining_date}
                onChange={handleChange}
                required
                className={`form-input ${errors.joining_date ? 'error' : ''}`}
              />
              {errors.joining_date && <span className="field-error">{errors.joining_date}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="therapy_needs">Therapy Needs</label>
            <textarea
              id="therapy_needs"
              name="therapy_needs"
              value={formData.therapy_needs}
              onChange={handleChange}
              className="form-textarea"
              rows={3}
              placeholder="Describe the student's therapy needs..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="special_requirements">Special Requirements</label>
            <textarea
              id="special_requirements"
              name="special_requirements"
              value={formData.special_requirements}
              onChange={handleChange}
              className="form-textarea"
              rows={3}
              placeholder="Any special requirements or accommodations..."
            />
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
              />
              Active
            </label>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="cancel-button">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="submit-button">
              {loading ? 'Saving...' : (student ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StudentForm;